import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { fetchMyScope, type UnifiedScope } from "@/lib/api/scope";

interface ScopeContextValue {
  scope: UnifiedScope | null;
  isLoading: boolean;

  /** Has any role/franchise at all. Logged-in users normally do. */
  hasAnyScope: boolean;
  /** No geographic restriction (super_admin / IP Owner). */
  isUnrestricted: boolean;
  /** True if the user is a franchise owner (any tier). */
  isFranchiseOwner: boolean;
  /** True if the user is an internal team member. */
  isInternalTeam: boolean;

  /** Country ids the user is allowed to see. Meaningful only when
   * !isUnrestricted — otherwise consumers should treat it as "all". */
  scopedCountryIds: number[];
  /** City ids the user is allowed to see. Same caveat as above. */
  scopedCityIds: number[];

  refetch: () => Promise<void>;
}

const defaultValue: ScopeContextValue = {
  scope: null,
  isLoading: true,
  hasAnyScope: false,
  isUnrestricted: false,
  isFranchiseOwner: false,
  isInternalTeam: false,
  scopedCountryIds: [],
  scopedCityIds: [],
  refetch: async () => {},
};

const ScopeContext = createContext<ScopeContextValue>(defaultValue);

export function ScopeProvider({ children }: { children: React.ReactNode }) {
  const [scope, setScope] = useState<UnifiedScope | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetchMyScope();
      setScope(res.scope);
    } catch {
      setScope(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo<ScopeContextValue>(() => ({
    scope,
    isLoading,
    hasAnyScope: scope !== null,
    isUnrestricted: scope?.is_unrestricted ?? false,
    isFranchiseOwner: scope?.source === "franchise_owner",
    isInternalTeam: scope?.source === "internal_team",
    scopedCountryIds: scope?.country_ids ?? [],
    scopedCityIds: scope?.city_ids ?? [],
    refetch: load,
  }), [scope, isLoading, load]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope() {
  return useContext(ScopeContext);
}
